// CONSIGNMENT TRACE — when the yarn was bought, and when the fabric was billed to RSR.
//
// Four of the seven steps are recorded. The other three are PRINTED AS BREAKS, not left
// blank: a blank column reads as "nothing happened", and what is true is "nobody wrote it
// down". The three are distinguished because they are different problems — one table exists
// and is unused, one table does not exist, one exists but cannot say what it dispatched.
//
// The KNP rides ALONGSIDE the knitting-run break and never in place of it. It is the
// authorised plan; putting its date in an execution column would answer a question nobody
// recorded, with a date that looks entirely plausible.
import React, { useState, useEffect } from 'react';
import api from '../services/api';

const C = { navy: '#3B3A8F', amber: '#B45309', red: '#A32D2D', green: '#166534', muted: '#999' };
const d = (v) => v ? String(v).slice(0, 10).split('-').reverse().slice(0, 2).join('/') + '/' + String(v).slice(2, 4) : null;

// A step that happened, a step that is only planned, or a step nobody recorded — and the
// third says WHY, because "not recorded" without a reason is just another blank.
function Step({ label, date, detail, plan, gap }) {
  const tone = gap ? C.muted : plan ? C.amber : C.green;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0', fontSize: 12 }}>
      <span style={{ width: 128, color: '#666', flexShrink: 0 }}>{label}</span>
      <span style={{ width: 66, fontWeight: 700, color: tone, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
        {gap ? '—' : (date || '—')}
      </span>
      <span style={{ color: gap ? C.muted : '#444', fontStyle: gap ? 'italic' : 'normal' }}>
        {plan && <span style={{ background: '#FEF3C7', color: C.amber, borderRadius: 10, padding: '0 6px', fontSize: 10, fontWeight: 700, marginRight: 6 }}>PLAN — authorisation, not execution</span>}
        {detail}
      </span>
    </div>
  );
}

export default function ConsignmentTrace() {
  const [rows, setRows] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [q, setQ] = useState('');
  const [only, setOnly] = useState(true);

  useEffect(() => {
    api.get('/deroi/trace').then(r => { setRows(r.data.data || []); setGaps(r.data.gaps || null); })
      .catch(() => setRows([]));
  }, []);

  if (!rows) return <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>;
  const shown = rows
    .filter(r => !only || r.yarn_pos.length || r.issues.length || r.knp)
    .filter(r => !q || [r.gpo_no, r.style_no, r.item_desc].join(' ').toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 10, lineHeight: 1.6, maxWidth: 900 }}>
        In-House GPOs only — Buy Outright has no yarn phase, and zeros down the whole left of the
        report would read as missing data rather than as a different route.
        {' '}<strong>{rows.length}</strong> in-house GPOs · <strong>{rows.filter(r => r.yarn_pos.length).length}</strong> with a yarn PO
        · <strong>{rows.filter(r => r.issues.length).length}</strong> issued to a knitter.
      </div>
      {gaps && (
        <div style={{ background: '#FBFBFD', border: '1px solid #E8E7E7', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 11.5, color: '#555', maxWidth: 900 }}>
          <strong style={{ color: C.navy }}>What this report cannot answer, and why</strong>
          {Object.entries(gaps).filter(([, g]) => !g.recorded).map(([k, g]) => (
            <div key={k} style={{ marginTop: 4 }}>
              <span style={{ fontWeight: 700 }}>{k.replace(/_/g, ' ')}</span> — {g.reason}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 GPO or style"
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 240 }} />
        <label style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
          <input type="checkbox" checked={only} onChange={e => setOnly(e.target.checked)} />
          {' '}Only GPOs with something recorded
        </label>
        <span style={{ fontSize: 12, color: C.muted }}>{shown.length} shown</span>
      </div>

      {shown.map(r => (
        <div key={r.gpo_no} style={{ border: '1px solid #E8E7E7', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 6 }}>
            GPO {r.gpo_no}
            <span style={{ fontWeight: 400, color: '#888' }}> · {[r.style_no, r.colour, r.item_desc].filter(Boolean).join(' · ') || '—'}</span>
          </div>

          {r.yarn_pos.length === 0
            ? <Step label="yarn bought" gap detail="no yarn PO reaches this GPO" />
            : r.yarn_pos.map((y, i) => (
              <Step key={i} label={i === 0 ? 'yarn bought' : ''} date={d(y.raised)}
                detail={<>
                  {y.po_no}
                  {y.status !== 'approved' && <span style={{ color: C.red, fontWeight: 700 }}> · {y.status}</span>}
                  {/* The clubbing belongs on the row that carries the date. A date repeating
                      across 28 GPOs is the truth about how the yarn was bought, and a reader
                      who meets it on 28 separate reports should not have to work that out. */}
                  {y.clubbed_gpos > 1 && <span style={{ background: '#EEF0FA', color: C.navy, borderRadius: 10, padding: '0 7px', fontSize: 10, fontWeight: 700, marginLeft: 6 }}>
                    one purchase, {y.clubbed_gpos} GPOs</span>}
                  {y.received ? <span style={{ color: C.green }}> · received {d(y.received)}</span>
                              : <span style={{ color: C.muted, fontStyle: 'italic' }}> · not received</span>}
                </>} />
            ))}

          {r.knp
            ? <Step label="knitting plan" date={d(r.knp.rsr_approved_at || r.knp.date)} plan
                detail={`${r.knp.knp_no} · ${r.knp.status}${r.knp.rsr_approved_at ? ' · RSR approved' : ' · not yet approved by RSR'}`} />
            : <Step label="knitting plan" gap detail="no KNP raised for this GPO" />}

          {r.issues.length === 0
            ? <Step label="issued to knitter" gap detail="no yarn issued" />
            : r.issues.map((it, i) => (
              <React.Fragment key={i}>
                <Step label={i === 0 ? 'issued to knitter' : ''} date={d(it.issued)}
                  detail={`${it.challan_no} → ${it.knitter || '—'} · ${it.yarn || '—'} · ${Number(it.issued_kg).toFixed(2)} kg`} />
              </React.Fragment>
            ))}

          {/* WHAT CAME BACK — three separate facts, never collapsed into one.
              This block replaces a single "greige returned / greige not returned" line that read
              yarn_returns and therefore described the wrong material. Yarn returned unknitted is
              not greige, and material still at the knitter is not material lost. */}
          {(r.reconcile || []).map((g, i) => {
            const atKnitter = Number(g.at_knitter_kg) || 0;
            return (
              <React.Fragment key={`rec${i}`}>
                {Number(g.greige_kg) > 0
                  ? <Step label={i === 0 ? 'greige back' : ''} detail={`${Number(g.greige_kg).toFixed(2)} kg returned as fabric`} />
                  : <Step label={i === 0 ? 'greige back' : ''} gap detail="no greige return recorded — lots/lot_members has never been written to" />}
                {Number(g.yarn_kg) > 0 && (
                  <Step label="" date={d(g.yarn_back_on)}
                    detail={`${Number(g.yarn_kg).toFixed(2)} kg yarn returned unknitted${g.reason ? ` · ${String(g.reason).split(';')[0]}` : ''}`} />
                )}
                {/* THE BALANCE IS STATED, AND STATED AS A LOCATION. It is not a discrepancy and
                    must not read as one: nothing here says material is missing, only that its
                    return has not been written down. */}
                {atKnitter > 0.0005 && (
                  <Step label="" detail={
                    <span style={{ color: C.navy }}>
                      <strong>{atKnitter.toFixed(2)} kg still with the knitter</strong>
                      {' '}— not missing; the greige return has not been recorded
                    </span>} />
                )}
              </React.Fragment>
            );
          })}

          {/* ALWAYS PRINTED. lots/lot_members exist and are empty, so this is never populated
              today — but the line must appear, because its absence is the finding. */}
          <Step label="knitting run" gap detail="not recorded — the run has no identity yet" />
          <Step label="issued to dyeing" gap detail="no record exists" />

          {r.dispatches.length === 0
            ? <><Step label="dispatched to RSR" gap detail="not recorded" />
                <Step label="invoiced to RSR" gap detail="not recorded" /></>
            : r.dispatches.map((x, i) => (
              <React.Fragment key={i}>
                <Step label={i === 0 ? 'dispatched to RSR' : ''} date={d(x.submitted)} detail={x.accepted ? `accepted ${d(x.accepted)}` : 'not accepted'} />
                <Step label="invoiced to RSR" date={d(x.invoice_date)} detail={x.invoice_no || '—'} />
              </React.Fragment>
            ))}
        </div>
      ))}
    </div>
  );
}
